import {
  isEmptyInputValue,
  toDisplayString,
  toDisplayValue,
} from "@/flow/utils/stepInputSummary";
import type { DisplayValue } from "@/flow/utils/stepInputSummary";

/**
 * Summarizes the custom fields a wizard flow declares, for the preview canvas.
 *
 * These are the flow-level definitions (`user_defined_variables`), not the per-step before/after
 * assignments — the wizard cannot write those. A custom field's value is invented by the wizard
 * and appears nowhere else on the canvas: a step only shows that it *reads* one. So this is the
 * single place a reviewer can check what the value actually is before approving.
 */

export interface CustomFieldRow {
  name: string;
  /** The value in the editor's notation, so a reference is tagged rather than shown raw. */
  display: DisplayValue;
}

/** One row per declared custom field, in authored order. Blank definitions are dropped. */
export function summarizeCustomFields(flowSpec: any): CustomFieldRow[] {
  const declared = flowSpec?.user_defined_variables;
  if (!declared || typeof declared !== "object" || Array.isArray(declared)) {
    return [];
  }

  return Object.entries(declared as Record<string, unknown>)
    .filter(([name, value]) => name.trim() && !isEmptyInputValue(value))
    .map(([name, value]) => ({
      name,
      display: toDisplayValue(toDisplayString(value)),
    }));
}
